/**
 * 🎉 FUN MODE - Avatar Animation Overlay
 * 
 * Playful avatar interactions for the desk booking system.
 * Creates a separate overlay layer with animations that don't affect the main SVG.
 */

// Configuration
const FUN_CONFIG = {
    gravity: 0.15, // Very slow gravity for bubble-like floating
    friction: 0.98,
    bounce: 0.7,
    floorY: 480,
    wallLeft: -80,
    wallRight: 470,
    collisionElasticity: 0.8,
    avatarRadius: 35,
    sparkleCount: 8,
    // Balloon floating behavior
    floatStrength: 0.15, // Random floating force
    floatFrequency: 0.02, // How often direction changes
    // Evasion behavior
    evasionDistance: 100, // Distance at which avatars start evading
    evasionStrength: 0.8, // How strongly they evade
    wanderStrength: 0.1, // Random wandering when idle
    // Glow color change
    glowChangeFrequency: 0.01 // How often glow color randomly changes
};

const GLOW_COLORS = [
    '#FFD700', // Gold
    '#FF6B35', // Orange
    '#00FF88', // Neon Green
    '#00BFFF', // Deep Sky Blue
    '#FF69B4', // Hot Pink
    '#9370DB', // Medium Purple
    '#00CED1', // Dark Turquoise
    '#FF4500', // Orange Red
];

// Fun expressions for various interactions
const EXPRESSIONS = {
    collision: ['🔥 POOF!', '💥 BAM!', '⚡ ZAP!', '💫 BONK!', '🌟 CRASH!'],
    thrown: ['Wheee!', 'Woohoo!', '🚀', 'Yippee!', 'Higher!'],
    grabbed: ['Hehe!', '😊', 'Hi there!', 'Pick me!', '👋'],
    released: ['Weee!', 'Haha!', '😆', 'Again!', 'So fun!'],
    floorHit: ['Ouch!', 'Oof!', '😵', 'Bonk!', '💫'],
    idle: ['😴', '💤', 'Zzz...', '...', '🙂']
};

// Dizzy star emojis
const DIZZY_EMOJIS = ['⭐', '✨', '💫', '🌟', '⚡'];

// State
let funModeActive = false;
let overlayGroup = null;
let avatarElements = {};
let ropeElements = {};
let avatarPhysics = {}; // { id: { x, y, vx, vy, originX, originY } }
let animationFrameId = null;
let draggedAvatar = null;
let dragOffset = { x: 0, y: 0 };

/**
 * Set Fun Mode state explicitly
 */
export function setFunMode(active) {
    if (funModeActive === active) return;

    funModeActive = active;

    if (funModeActive) {
        createOverlay();
        startPhysics();
        setupDragListeners();
    } else {
        removeDragListeners();
        stopPhysics();
        removeOverlay();
    }

    return funModeActive;
}

/**
 * Force update Fun Mode (refresh overlay)
 */
export function updateFunMode() {
    if (!funModeActive) return;
    removeDragListeners();
    stopPhysics();
    removeOverlay();
    createOverlay();
    startPhysics();
    setupDragListeners();
}

/**
 * Toggle Fun Mode on/off
 */
export function toggleFunMode() {
    return setFunMode(!funModeActive);
}

/**
 * Create the animation overlay layer - one per desk group SVG
 */
function createOverlay() {
    // Get ALL desk group SVGs (we now have multiple)
    const allDeskGroupSvgs = document.querySelectorAll('.desk-group-svg');
    if (allDeskGroupSvgs.length === 0) return;

    // Track all overlays we create
    const overlays = [];
    let globalAvatarIndex = 0;

    // Create an overlay for EACH desk group SVG
    allDeskGroupSvgs.forEach((svg, svgIndex) => {
        const bookingAvatars = svg.querySelectorAll('.booking-avatar');
        if (bookingAvatars.length === 0) return; // Skip SVGs with no avatars

        // Create overlay group for THIS SVG
        const localOverlay = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        localOverlay.setAttribute('class', 'fun-overlay');
        localOverlay.setAttribute('id', `avatar-fun-overlay-${svgIndex}`);

        // Add filter definitions for effects (each SVG needs its own)
        const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
        defs.innerHTML = `
            <filter id="fun-glow-${svgIndex}" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="3" result="blur"/>
                <feMerge>
                    <feMergeNode in="blur"/>
                    <feMergeNode in="SourceGraphic"/>
                </feMerge>
            </filter>
            <filter id="fun-shadow-${svgIndex}" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="5" stdDeviation="3" flood-color="rgba(0,0,0,0.3)"/>
            </filter>
            <filter id="fun-fire-${svgIndex}" x="-100%" y="-100%" width="300%" height="300%">
                <feGaussianBlur stdDeviation="4" result="blur"/>
                <feColorMatrix type="matrix" values="1 0 0 0 0  0.5 0.2 0 0 0  0 0 0 0 0  0 0 0 1 0" result="fire"/>
                <feMerge>
                    <feMergeNode in="fire"/>
                    <feMergeNode in="SourceGraphic"/>
                </feMerge>
            </filter>
            ${GLOW_COLORS.map((color, i) => `
            <filter id="avatar-glow-${svgIndex}-${i}" x="-100%" y="-100%" width="300%" height="300%">
                <feGaussianBlur stdDeviation="6" result="blur"/>
                <feFlood flood-color="${color}" flood-opacity="0.8"/>
                <feComposite in2="blur" operator="in"/>
                <feMerge>
                    <feMergeNode/>
                    <feMergeNode in="SourceGraphic"/>
                </feMerge>
            </filter>
            `).join('')}
        `;
        localOverlay.appendChild(defs);

        // Create animated clones for avatars in THIS SVG only
        bookingAvatars.forEach((avatar) => {
            const clone = createAnimatedAvatar(avatar, globalAvatarIndex, svgIndex, localOverlay);
            if (clone) {
                localOverlay.appendChild(clone);
                // Hide original avatar
                avatar.style.opacity = '0';
                avatar.style.pointerEvents = 'none';
                globalAvatarIndex++;
            }
        });

        // Add overlay to this SVG
        svg.appendChild(localOverlay);
        overlays.push(localOverlay);
    });

    // Store the first overlay as the main one for compatibility
    overlayGroup = overlays[0] || null;
}

/**
 * Create an animated avatar copy
 */
function createAnimatedAvatar(originalAvatar, index, svgIndex = 0, overlay = null) {
    // Calculate absolute position by summing transforms up to the SVG root
    let curr = originalAvatar;
    let x = 0;
    let y = 0;

    // Traverse up to find all translates until we hit the main SVG or desk-group-svg
    while (curr && curr.tagName !== 'svg' && curr.classList && !curr.classList.contains('desk-group-svg')) {
        const transform = curr.getAttribute('transform');
        if (transform) {
            const match = transform.match(/translate\(([^,]+)(?:,\s*([^)]+))?\)/);
            if (match) {
                x += parseFloat(match[1] || 0);
                y += parseFloat(match[2] || 0);
            }
        }
        curr = curr.parentNode;
    }

    const avatarId = `fun-avatar-${svgIndex}-${index}`;
    const glowIndex = index % GLOW_COLORS.length;

    // Create avatar group
    const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    group.setAttribute('id', avatarId);
    group.setAttribute('class', 'fun-avatar');
    group.setAttribute('transform', `translate(${x}, ${y})`);
    group.setAttribute('filter', `url(#avatar-glow-${svgIndex}-${glowIndex})`);
    group.dataset.originX = x;
    group.dataset.originY = y;
    group.dataset.glowIndex = glowIndex;
    group.dataset.svgIndex = svgIndex;
    group.dataset.onFire = 'false';

    // Clone all contents of the avatar (Circle, Image, etc.) but EXCLUDE text labels
    Array.from(originalAvatar.children).forEach(child => {
        // Skip cloning text labels (avatar-name, avatar-time) to prevent duplication
        if (child.tagName === 'text' && (child.classList.contains('avatar-name') || child.classList.contains('avatar-time'))) {
            return; // Skip this element
        }

        const clone = child.cloneNode(true);
        if (clone.tagName === 'circle' && clone.classList.contains('avatar-border')) {
            clone.setAttribute('class', 'fun-avatar-circle');
            clone.setAttribute('stroke', GLOW_COLORS[glowIndex]);
            clone.setAttribute('stroke-width', '4');
        }
        group.appendChild(clone);
    });

    // Create rope back to original position
    if (overlay) {
        const rope = createRope(x, y, x, y);
        rope.setAttribute('id', `fun-rope-${svgIndex}-${index}`);
        overlay.insertBefore(rope, overlay.firstChild); // Rope behind avatars

        avatarElements[avatarId] = group;
        ropeElements[avatarId] = rope;
    }

    // Add click handler for interaction
    group.style.cursor = 'grab';

    return group;
}

/**
 * Create a rope/chain SVG path
 */
function createRope(x1, y1, x2, y2) {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('class', 'fun-rope');
    path.setAttribute('d', `M ${x1} ${y1} L ${x2} ${y2}`);
    path.setAttribute('stroke', '#8B4513');
    path.setAttribute('stroke-width', '3');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-dasharray', '8,4');
    return path;
}

/**
 * Update rope path
 */
function updateRope(ropeElement, x1, y1, x2, y2) {
    // Simple straight line
    const d = `M ${x1} ${y1} L ${x2} ${y2}`;
    ropeElement.setAttribute('d', d);
    ropeElement.setAttribute('stroke', '#555'); // Darker rope
    ropeElement.setAttribute('stroke-dasharray', '5,5'); // Dashed look
}

/**
 * Setup drag event listeners
 */
function setupDragListeners() {
    const svg = document.querySelector('.desk-group-svg');
    if (!svg) return;

    svg.addEventListener('mousedown', handleMouseDown);
    svg.addEventListener('mousemove', handleMouseMove);
    svg.addEventListener('mouseup', handleMouseUp);
    svg.addEventListener('mouseleave', handleMouseUp);

    // Touch support
    svg.addEventListener('touchstart', handleTouchStart, { passive: false });
    svg.addEventListener('touchmove', handleTouchMove, { passive: false });
    svg.addEventListener('touchend', handleTouchEnd);
}

/**
 * Remove drag event listeners
 */
function removeDragListeners() {
    const svg = document.querySelector('.desk-group-svg');
    if (!svg) return;

    svg.removeEventListener('mousedown', handleMouseDown);
    svg.removeEventListener('mousemove', handleMouseMove);
    svg.removeEventListener('mouseup', handleMouseUp);
    svg.removeEventListener('mouseleave', handleMouseUp);
    svg.removeEventListener('touchstart', handleTouchStart);
    svg.removeEventListener('touchmove', handleTouchMove);
    svg.removeEventListener('touchend', handleTouchEnd);
}

/**
 * Get SVG coordinates from mouse/touch event
 */
function getSVGCoords(svg, clientX, clientY) {
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x: svgP.x, y: svgP.y };
}

/**
 * Handle mouse down - start dragging
 */
function handleMouseDown(e) {
    const svg = e.currentTarget;
    const coords = getSVGCoords(svg, e.clientX, e.clientY);

    // Find if we clicked on an avatar
    for (const [id, p] of Object.entries(avatarPhysics)) {
        const dx = coords.x - p.x;
        const dy = coords.y - p.y;
        if (Math.sqrt(dx * dx + dy * dy) < p.radius) {
            draggedAvatar = id;
            dragOffset.x = dx;
            dragOffset.y = dy;
            p.element.style.cursor = 'grabbing';
            // Stop velocity while dragging
            p.vx = 0;
            p.vy = 0;
            // Show grabbed expression
            addSpeechBubble(p.x, p.y - p.radius - 20, EXPRESSIONS.grabbed);
            e.preventDefault();
            break;
        }
    }
}

/**
 * Handle mouse move - drag avatar
 */
function handleMouseMove(e) {
    if (!draggedAvatar) return;

    const svg = e.currentTarget;
    const coords = getSVGCoords(svg, e.clientX, e.clientY);
    const p = avatarPhysics[draggedAvatar];

    if (p) {
        // Calculate velocity from movement
        const newX = coords.x - dragOffset.x;
        const newY = coords.y - dragOffset.y;
        p.vx = (newX - p.x) * 0.5; // Store velocity for release
        p.vy = (newY - p.y) * 0.5;
        p.x = newX;
        p.y = newY;
    }
}

/**
 * Handle mouse up - release avatar
 */
function handleMouseUp(e) {
    if (draggedAvatar) {
        const p = avatarPhysics[draggedAvatar];
        if (p) {
            p.element.style.cursor = 'grab';
            // Boost velocity on release
            p.vx *= 3;
            p.vy *= 3;
            // Show release expression if thrown with force
            const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
            if (speed > 5) {
                addSpeechBubble(p.x, p.y - p.radius - 20, EXPRESSIONS.thrown);
            } else {
                addSpeechBubble(p.x, p.y - p.radius - 20, EXPRESSIONS.released);
            }
        }
        draggedAvatar = null;
    }
}

/**
 * Handle touch start
 */
function handleTouchStart(e) {
    if (e.touches.length === 1) {
        const touch = e.touches[0];
        const fakeEvent = { currentTarget: e.currentTarget, clientX: touch.clientX, clientY: touch.clientY, preventDefault: () => e.preventDefault() };
        handleMouseDown(fakeEvent);
    }
}

/**
 * Handle touch move
 */
function handleTouchMove(e) {
    if (e.touches.length === 1 && draggedAvatar) {
        e.preventDefault();
        const touch = e.touches[0];
        const fakeEvent = { currentTarget: e.currentTarget, clientX: touch.clientX, clientY: touch.clientY };
        handleMouseMove(fakeEvent);
    }
}

/**
 * Handle touch end
 */
function handleTouchEnd(e) {
    handleMouseUp(e);
}

/**
 * Start physics simulation
 */
function startPhysics() {
    // Initialize physics state for all avatars
    Object.entries(avatarElements).forEach(([id, element], index) => {
        const originX = parseFloat(element.dataset.originX);
        const originY = parseFloat(element.dataset.originY);

        avatarPhysics[id] = {
            id,
            x: originX,
            y: originY,
            vx: (Math.random() - 0.5) * 5, // Small random horizontal start
            vy: 0,
            originX,
            originY,
            radius: FUN_CONFIG.avatarRadius,
            element,
            fireTimer: 0
        };
    });

    if (!animationFrameId) {
        updatePhysics();
    }
}

/**
 * Stop physics simulation
 */
function stopPhysics() {
    if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
    }
    avatarPhysics = {};
}

/**
 * Main Physics Loop
 */
function updatePhysics() {
    if (!funModeActive) return;

    Object.values(avatarPhysics).forEach(p => {
        // Skip physics for dragged avatar
        if (draggedAvatar === p.id) return;

        // === GENTLE BUOYANCY (no wobble) ===
        // Very gentle upward drift
        p.vy -= FUN_CONFIG.floatStrength * 0.3;

        // === EVASION BEHAVIOR ===
        // Check for nearby avatars and try to dodge
        const physicsArray = Object.values(avatarPhysics);
        physicsArray.forEach(other => {
            if (other.id === p.id) return;
            if (draggedAvatar === other.id) {
                // Evade the dragged avatar more aggressively!
                const dx = p.x - other.x;
                const dy = p.y - other.y;
                const dist = Math.sqrt(dx * dx + dy * dy);

                if (dist < FUN_CONFIG.evasionDistance * 1.5 && dist > 0) {
                    // Run away from the dragged avatar!
                    const evadeForce = FUN_CONFIG.evasionStrength * 2 * (1 - dist / (FUN_CONFIG.evasionDistance * 1.5));
                    p.vx += (dx / dist) * evadeForce;
                    p.vy += (dy / dist) * evadeForce;
                }
            } else {
                // Normal evasion from other avatars
                const dx = p.x - other.x;
                const dy = p.y - other.y;
                const dist = Math.sqrt(dx * dx + dy * dy);

                if (dist < FUN_CONFIG.evasionDistance && dist > 0) {
                    // Gently move away
                    const evadeForce = FUN_CONFIG.evasionStrength * (1 - dist / FUN_CONFIG.evasionDistance);
                    p.vx += (dx / dist) * evadeForce * 0.3;
                    p.vy += (dy / dist) * evadeForce * 0.3;
                }
            }
        });

        // === BUBBLE FLOAT EFFECT ===
        // When hit, avatar floats up slowly like a bubble
        if (p.bubbleFloat && p.bubbleTimer > 0) {
            p.bubbleTimer--;
            // Very reduced gravity while floating (almost weightless)
            p.vy += FUN_CONFIG.gravity * 0.1;
        } else {
            // Normal gravity
            p.bubbleFloat = false;
            // Apply Gravity (reduced for balloon effect)
            p.vy += FUN_CONFIG.gravity;
        }

        // Apply Velocity
        p.x += p.vx;
        p.y += p.vy;

        // Apply Friction (Air resistance)
        p.vx *= FUN_CONFIG.friction;
        p.vy *= FUN_CONFIG.friction;

        // Floor Collision
        if (p.y > FUN_CONFIG.floorY - p.radius) {
            const impactSpeed = Math.abs(p.vy);
            p.y = FUN_CONFIG.floorY - p.radius;
            p.vy *= -FUN_CONFIG.bounce;

            // If near stop on floor, allow small roll
            if (Math.abs(p.vy) < 1) p.vy = 0;

            // Stronger ground friction
            p.vx *= 0.95;

            // Add dizzy stars on hard impact
            if (impactSpeed > 10 && Math.random() > 0.5) {
                addDizzyStars(p.x, p.y - p.radius);
                addSpeechBubble(p.x, p.y - p.radius - 30, EXPRESSIONS.floorHit);
            }
        }

        // Wall Collisions
        if (p.x < FUN_CONFIG.wallLeft + p.radius) {
            p.x = FUN_CONFIG.wallLeft + p.radius;
            p.vx *= -FUN_CONFIG.bounce;
        } else if (p.x > FUN_CONFIG.wallRight - p.radius) {
            p.x = FUN_CONFIG.wallRight - p.radius;
            p.vx *= -FUN_CONFIG.bounce;
        }

        // Handle fire timer
        if (p.fireTimer > 0) {
            p.fireTimer--;
            if (p.fireTimer <= 0) {
                // Remove fire effect - apply random new glow color
                const svgIndex = p.element.dataset.svgIndex || 0;
                const newGlowIndex = Math.floor(Math.random() * GLOW_COLORS.length);
                p.element.dataset.glowIndex = newGlowIndex;
                p.element.setAttribute('filter', `url(#avatar-glow-${svgIndex}-${newGlowIndex})`);
                p.element.dataset.onFire = 'false';
                // Update circle stroke color
                const circle = p.element.querySelector('.fun-avatar-circle');
                if (circle) {
                    circle.setAttribute('stroke', GLOW_COLORS[newGlowIndex]);
                }
            }
        }

        // Random glow color change (when not on fire)
        if (p.element.dataset.onFire !== 'true' && Math.random() < FUN_CONFIG.glowChangeFrequency) {
            const svgIndex = p.element.dataset.svgIndex || 0;
            const newGlowIndex = Math.floor(Math.random() * GLOW_COLORS.length);
            p.element.dataset.glowIndex = newGlowIndex;
            p.element.setAttribute('filter', `url(#avatar-glow-${svgIndex}-${newGlowIndex})`);
            // Update circle stroke color
            const circle = p.element.querySelector('.fun-avatar-circle');
            if (circle) {
                circle.setAttribute('stroke', GLOW_COLORS[newGlowIndex]);
            }
        }
    });

    // Avatar vs Avatar Collisions
    const physicsArray = Object.values(avatarPhysics);
    for (let i = 0; i < physicsArray.length; i++) {
        for (let j = i + 1; j < physicsArray.length; j++) {
            resolveCollision(physicsArray[i], physicsArray[j]);
        }
    }

    // Render Updates
    physicsArray.forEach(p => {
        // Update Transform (Translation + slight rotation based on X velocity)
        const rotation = p.vx * 2;
        p.element.setAttribute('transform', `translate(${p.x}, ${p.y}) rotate(${rotation})`);

        // Update Rope
        const rope = ropeElements[p.id];
        if (rope) {
            updateRope(rope, p.originX, p.originY, p.x, p.y);
        }
    });

    animationFrameId = requestAnimationFrame(updatePhysics);
}

/**
 * Resolve collision between two avatars
 */
function resolveCollision(p1, p2) {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const minDist = p1.radius + p2.radius;

    if (distance < minDist && distance > 0) {
        // Collision detected!

        // Calculate collision normal
        const nx = dx / distance;
        const ny = dy / distance;

        // Separation (prevent overlap)
        const overlap = minDist - distance;
        const moveX = nx * overlap * 0.5;
        const moveY = ny * overlap * 0.5;

        // Apply position correction
        p1.x -= moveX;
        p1.y -= moveY;
        p2.x += moveX;
        p2.y += moveY;

        // Calculate relative velocity
        const rvx = p2.vx - p1.vx;
        const rvy = p2.vy - p1.vy;

        // Velocity along normal
        const velAlongNormal = rvx * nx + rvy * ny;

        // Do not resolve if velocities are separating
        if (velAlongNormal > 0) return;

        // Impulse scalar
        let j = -(1 + FUN_CONFIG.collisionElasticity) * velAlongNormal;
        j /= 2; // Assuming equal mass

        // Apply impulse
        const impulseX = j * nx;
        const impulseY = j * ny;

        p1.vx -= impulseX;
        p1.vy -= impulseY;
        p2.vx += impulseX;
        p2.vy += impulseY;

        // Add sparkle effect if impact is significant
        if (Math.abs(j) > 2) {
            const contactX = p1.x + (nx * p1.radius);
            const contactY = p1.y + (ny * p1.radius);

            // Determine attacker (higher momentum) and victim
            const p1Momentum = Math.sqrt(p1.vx * p1.vx + p1.vy * p1.vy);
            const p2Momentum = Math.sqrt(p2.vx * p2.vx + p2.vy * p2.vy);
            const attacker = p1Momentum > p2Momentum ? p1 : p2;
            const victim = p1Momentum > p2Momentum ? p2 : p1;

            // Limit frequency of effects
            if (Math.random() > 0.5) {
                addCollisionEffect(contactX, contactY);
                // Victim gets fire ring effect AND angel halo (they're innocent!)
                addFireRing(victim);
                addAngelHalo(victim);
                setAvatarOnFire(victim);
                // Attacker gets devil horns
                addDevilHorns(attacker);
            }
        }
    }
}

/**
 * Set avatar on fire effect and make it float like a bubble
 */
function setAvatarOnFire(p) {
    const svgIndex = p.element.dataset.svgIndex || 0;
    p.element.setAttribute('filter', `url(#fun-fire-${svgIndex})`);
    p.element.dataset.onFire = 'true';
    p.fireTimer = 60; // ~1 second at 60fps

    // Bubble float effect - pop up and float
    p.vy = -8 - Math.random() * 5; // Strong upward pop
    p.bubbleFloat = true; // Mark as floating bubble
    p.bubbleTimer = 120; // Float for ~2 seconds
}

/**
 * Add animated fire ring around victim avatar
 */
function addFireRing(p) {
    if (!overlayGroup) return;

    const fireRing = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    fireRing.setAttribute('class', 'fire-ring');
    fireRing.setAttribute('transform', `translate(${p.x}, ${p.y})`);

    // Create multiple flame elements around the ring
    const flameCount = 12;
    for (let i = 0; i < flameCount; i++) {
        const angle = (i / flameCount) * Math.PI * 2;
        const flame = document.createElementNS('http://www.w3.org/2000/svg', 'ellipse');
        const flameX = Math.cos(angle) * (p.radius + 8);
        const flameY = Math.sin(angle) * (p.radius + 8);
        flame.setAttribute('cx', flameX);
        flame.setAttribute('cy', flameY);
        flame.setAttribute('rx', '8');
        flame.setAttribute('ry', '12');
        flame.setAttribute('fill', ['#FF4500', '#FF6600', '#FFD700', '#FF0000'][i % 4]);
        flame.setAttribute('transform', `rotate(${(angle * 180 / Math.PI) + 90}, ${flameX}, ${flameY})`);
        fireRing.appendChild(flame);
    }

    overlayGroup.appendChild(fireRing);

    // Animate fire ring
    let startTime = null;
    function animateFireRing(timestamp) {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / 1500, 1); // 1.5 second duration

        // Scale up and fade out
        const scale = 1 + progress * 0.5;
        const opacity = 1 - progress;
        fireRing.setAttribute('transform', `translate(${p.x}, ${p.y}) scale(${scale})`);
        fireRing.setAttribute('opacity', opacity);

        // Rotate flames
        const rotation = elapsed * 0.2;
        fireRing.style.transformOrigin = 'center';

        if (progress < 1) {
            requestAnimationFrame(animateFireRing);
        } else {
            fireRing.remove();
        }
    }
    requestAnimationFrame(animateFireRing);
}

/**
 * Add devil horns to the attacker avatar
 */
function addDevilHorns(p) {
    if (!overlayGroup) return;

    const hornsGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    hornsGroup.setAttribute('class', 'devil-horns');
    hornsGroup.setAttribute('transform', `translate(${p.x}, ${p.y - p.radius - 5})`);

    // Left horn
    const leftHorn = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    leftHorn.setAttribute('d', 'M -15 0 Q -20 -25 -8 -35 Q -12 -20 -10 0 Z');
    leftHorn.setAttribute('fill', '#8B0000');
    leftHorn.setAttribute('stroke', '#4A0000');
    leftHorn.setAttribute('stroke-width', '1');
    hornsGroup.appendChild(leftHorn);

    // Right horn
    const rightHorn = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    rightHorn.setAttribute('d', 'M 15 0 Q 20 -25 8 -35 Q 12 -20 10 0 Z');
    rightHorn.setAttribute('fill', '#8B0000');
    rightHorn.setAttribute('stroke', '#4A0000');
    rightHorn.setAttribute('stroke-width', '1');
    hornsGroup.appendChild(rightHorn);

    // Evil emoji
    const evilEmoji = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    evilEmoji.setAttribute('x', '0');
    evilEmoji.setAttribute('y', '-40');
    evilEmoji.setAttribute('text-anchor', 'middle');
    evilEmoji.setAttribute('font-size', '20');
    evilEmoji.textContent = '😈';
    hornsGroup.appendChild(evilEmoji);

    overlayGroup.appendChild(hornsGroup);

    // Animate horns
    let startTime = null;
    function animateHorns(timestamp) {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / 2000, 1); // 2 second duration

        // Follow avatar position and fade out
        const opacity = progress < 0.7 ? 1 : 1 - ((progress - 0.7) / 0.3);
        hornsGroup.setAttribute('transform', `translate(${p.x}, ${p.y - p.radius - 5})`);
        hornsGroup.setAttribute('opacity', opacity);

        if (progress < 1) {
            requestAnimationFrame(animateHorns);
        } else {
            hornsGroup.remove();
        }
    }
    requestAnimationFrame(animateHorns);
}

/**
 * Add angel halo above peaceful/victim avatar
 */
function addAngelHalo(p) {
    if (!overlayGroup) return;

    const haloGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    haloGroup.setAttribute('class', 'angel-halo');
    haloGroup.setAttribute('transform', `translate(${p.x}, ${p.y - p.radius - 15})`);

    // Golden halo ring (ellipse for 3D effect)
    const halo = document.createElementNS('http://www.w3.org/2000/svg', 'ellipse');
    halo.setAttribute('cx', '0');
    halo.setAttribute('cy', '0');
    halo.setAttribute('rx', '25');
    halo.setAttribute('ry', '8');
    halo.setAttribute('fill', 'none');
    halo.setAttribute('stroke', '#FFD700');
    halo.setAttribute('stroke-width', '4');
    halo.setAttribute('filter', 'url(#fun-glow)');
    haloGroup.appendChild(halo);

    // Inner glow
    const innerHalo = document.createElementNS('http://www.w3.org/2000/svg', 'ellipse');
    innerHalo.setAttribute('cx', '0');
    innerHalo.setAttribute('cy', '0');
    innerHalo.setAttribute('rx', '20');
    innerHalo.setAttribute('ry', '6');
    innerHalo.setAttribute('fill', 'none');
    innerHalo.setAttribute('stroke', '#FFFACD');
    innerHalo.setAttribute('stroke-width', '2');
    haloGroup.appendChild(innerHalo);

    // Angel emoji
    const angelEmoji = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    angelEmoji.setAttribute('x', '0');
    angelEmoji.setAttribute('y', '-20');
    angelEmoji.setAttribute('text-anchor', 'middle');
    angelEmoji.setAttribute('font-size', '18');
    angelEmoji.textContent = '😇';
    haloGroup.appendChild(angelEmoji);

    overlayGroup.appendChild(haloGroup);

    // Animate halo
    let startTime = null;
    function animateHalo(timestamp) {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / 2500, 1); // 2.5 second duration

        // Gentle bobbing motion
        const bobY = Math.sin(elapsed * 0.005) * 3;
        const opacity = progress < 0.8 ? 1 : 1 - ((progress - 0.8) / 0.2);

        haloGroup.setAttribute('transform', `translate(${p.x}, ${p.y - p.radius - 15 + bobY})`);
        haloGroup.setAttribute('opacity', opacity);

        if (progress < 1) {
            requestAnimationFrame(animateHalo);
        } else {
            haloGroup.remove();
        }
    }
    requestAnimationFrame(animateHalo);
}

/**
 * Add collision sparkle effects
 */
function addCollisionEffect(x, y) {
    if (!overlayGroup) return;

    // Add collision text with random expression
    const poof = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    poof.setAttribute('x', x);
    poof.setAttribute('y', y - 40);
    poof.setAttribute('text-anchor', 'middle');
    poof.setAttribute('fill', '#FF0000'); // RED color
    poof.setAttribute('font-size', '28');
    poof.setAttribute('font-weight', 'bold');
    poof.setAttribute('filter', 'url(#fun-glow)');
    poof.textContent = EXPRESSIONS.collision[Math.floor(Math.random() * EXPRESSIONS.collision.length)];
    overlayGroup.appendChild(poof);

    // Add fire sparkles
    for (let i = 0; i < FUN_CONFIG.sparkleCount; i++) {
        const angle = (i / FUN_CONFIG.sparkleCount) * Math.PI * 2;
        const sparkle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        sparkle.setAttribute('cx', x);
        sparkle.setAttribute('cy', y);
        sparkle.setAttribute('r', '6');
        sparkle.setAttribute('fill', ['#FF4500', '#FF6600', '#FF0000', '#FFD700'][i % 4]); // Fire colors
        overlayGroup.appendChild(sparkle);

        // Animate sparkle outward
        let startTime = null;
        function animateSparkle(timestamp) {
            if (!startTime) startTime = timestamp;
            const elapsed = timestamp - startTime;
            const progress = Math.min(elapsed / 600, 1);

            const distance = progress * 60;
            const sparkleX = x + Math.cos(angle) * distance;
            const sparkleY = y + Math.sin(angle) * distance - progress * 40;
            const opacity = 1 - progress;

            sparkle.setAttribute('cx', sparkleX);
            sparkle.setAttribute('cy', sparkleY);
            sparkle.setAttribute('opacity', opacity);
            sparkle.setAttribute('r', 6 * (1 - progress * 0.3));

            if (progress < 1) {
                requestAnimationFrame(animateSparkle);
            } else {
                sparkle.remove();
            }
        }
        requestAnimationFrame(animateSparkle);
    }

    // Remove poof text after animation
    setTimeout(() => poof.remove(), 800);
}

/**
 * Add speech bubble with random expression from category
 */
function addSpeechBubble(x, y, expressionArray) {
    if (!overlayGroup) return;

    const text = expressionArray[Math.floor(Math.random() * expressionArray.length)];

    // Create bubble background
    const bubble = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    bubble.setAttribute('class', 'speech-bubble');

    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('x', x - 40);
    rect.setAttribute('y', y - 25);
    rect.setAttribute('width', '80');
    rect.setAttribute('height', '30');
    rect.setAttribute('rx', '15');
    rect.setAttribute('fill', 'white');
    rect.setAttribute('stroke', '#333');
    rect.setAttribute('stroke-width', '2');
    bubble.appendChild(rect);

    // Create tail
    const tail = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    tail.setAttribute('points', `${x - 5},${y + 5} ${x + 5},${y + 5} ${x},${y + 15}`);
    tail.setAttribute('fill', 'white');
    tail.setAttribute('stroke', '#333');
    tail.setAttribute('stroke-width', '2');
    bubble.appendChild(tail);

    // Create text
    const textEl = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    textEl.setAttribute('x', x);
    textEl.setAttribute('y', y - 5);
    textEl.setAttribute('text-anchor', 'middle');
    textEl.setAttribute('fill', '#333');
    textEl.setAttribute('font-size', '14');
    textEl.setAttribute('font-weight', 'bold');
    textEl.textContent = text;
    bubble.appendChild(textEl);

    overlayGroup.appendChild(bubble);

    // Animate float up and fade out
    let startTime = null;
    function animateBubble(timestamp) {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / 1000, 1);

        const floatY = y - 40 - progress * 30;
        bubble.setAttribute('transform', `translate(0, ${-progress * 30})`);
        bubble.setAttribute('opacity', 1 - progress);

        if (progress < 1) {
            requestAnimationFrame(animateBubble);
        } else {
            bubble.remove();
        }
    }
    requestAnimationFrame(animateBubble);
}

/**
 * Add dizzy stars around avatar
 */
function addDizzyStars(x, y) {
    if (!overlayGroup) return;

    const starCount = 5;
    const starGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    starGroup.setAttribute('class', 'dizzy-stars');

    for (let i = 0; i < starCount; i++) {
        const angle = (i / starCount) * Math.PI * 2;
        const star = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        star.setAttribute('text-anchor', 'middle');
        star.setAttribute('font-size', '16');
        star.textContent = DIZZY_EMOJIS[i % DIZZY_EMOJIS.length];
        star.dataset.angle = angle;
        star.dataset.radius = 25 + Math.random() * 10;
        starGroup.appendChild(star);
    }

    overlayGroup.appendChild(starGroup);

    // Animate stars orbiting
    let startTime = null;
    function animateStars(timestamp) {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / 1500, 1);
        const rotation = elapsed * 0.005; // Rotation speed

        Array.from(starGroup.children).forEach((star, i) => {
            const baseAngle = parseFloat(star.dataset.angle);
            const radius = parseFloat(star.dataset.radius);
            const currentAngle = baseAngle + rotation;
            const starX = x + Math.cos(currentAngle) * radius;
            const starY = y - 20 + Math.sin(currentAngle) * radius * 0.5; // Elliptical orbit
            star.setAttribute('x', starX);
            star.setAttribute('y', starY);
        });

        starGroup.setAttribute('opacity', 1 - progress);

        if (progress < 1) {
            requestAnimationFrame(animateStars);
        } else {
            starGroup.remove();
        }
    }
    requestAnimationFrame(animateStars);
}

/**
 * Remove the overlay layer
 */
function removeOverlay() {
    // Restore ALL booking avatars from ALL desk group SVGs (not just the first one)
    const allDeskGroupSvgs = document.querySelectorAll('.desk-group-svg');
    allDeskGroupSvgs.forEach(svg => {
        const bookingAvatars = svg.querySelectorAll('.booking-avatar');
        bookingAvatars.forEach(avatar => {
            avatar.style.opacity = '1';
            avatar.style.pointerEvents = ''; // Restore pointer events
        });

        // Remove all fun-overlay groups from this SVG
        const overlays = svg.querySelectorAll('.fun-overlay');
        overlays.forEach(overlay => overlay.remove());
    });

    overlayGroup = null;
    avatarElements = {};
    ropeElements = {};
    draggedAvatar = null;
    stopPhysics();
}

/**
 * Check if fun mode is active
 */
export function isFunModeActive() {
    return funModeActive;
}

export default {
    toggleFunMode,
    isFunModeActive
};

